import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonDropdown from '@rio-cloud/rio-uikit/ButtonDropdown';
import useToggle from '@rio-cloud/rio-uikit/useToggle';
import classNames from 'classnames';

import { gaPush, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';
import { AccessInformationDialog } from './AccessInformationDialog';
import { LINK_CUSTOM_RULE, LINK_VEHICLE_DOWNTIME_TEMPLATE, LINK_VEHICLE_POSITION_TEMPLATE } from './eventMonitorLinks';

type NotificationsToolbarProps = {
    showReadMessages: boolean;
    hasUnreadMessages: boolean;
    onToggleReadMessages: () => void;
    onMarkAllAsRead: () => void;
    isVehicleMode: boolean;
};

export const NotificationsToolbar = (props: NotificationsToolbarProps) => {
    const { showReadMessages, onToggleReadMessages, onMarkAllAsRead, hasUnreadMessages, isVehicleMode } = props;

    const [targetLink, setTargetLink] = useState(LINK_CUSTOM_RULE);

    const { isToggled: isRuleInfoDialogShown, toggle: toggleRuleInfoDialog } = useToggle();

    const toolbarClassNames = classNames(
        'display-flex',
        'justify-content-between',
        !isVehicleMode && 'padding-y-5 padding-x-5',
        isVehicleMode ? 'margin-bottom-10' : 'margin-bottom-15 border border-bottom-only border-color-lighter'
    );

    const handleNewRule = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule',
            action: 'New rule dropdown button clicked',
        });
    };

    const handleCustomRule = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule',
            action: 'Custom rule option clicked',
        });
        setTargetLink(LINK_CUSTOM_RULE);
        toggleRuleInfoDialog();
    };

    const handleVehicleDowntime = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule',
            action: 'Vehicle downtime option clicked',
        });
        setTargetLink(LINK_VEHICLE_DOWNTIME_TEMPLATE);
        toggleRuleInfoDialog();
    };

    const handleVehiclePosition = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule',
            action: 'Vehicle position option clicked',
        });
        setTargetLink(LINK_VEHICLE_POSITION_TEMPLATE);
        toggleRuleInfoDialog();
    };

    const handleCloseRuleDialog = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule dialog',
            action: 'Closed new rule dialog',
        });
        toggleRuleInfoDialog();
    };

    const handleAcknowledgedRuleDialog = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'New rule dialog',
            action: `Opened rule editor via dialog with link ${targetLink}`,
        });
        toggleRuleInfoDialog();
    };

    const handleToggleReadMessages = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'Display options',
            action: showReadMessages ? 'Hide read notifications' : 'Show read notifications',
        });
        onToggleReadMessages();
    };

    const handleMarkAllAsRead = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'Display options',
            action: 'Marked all as read',
        });
        onMarkAllAsRead();
    };

    const handleOpenOptions = () => {
        gaPush({
            category: TRACKING_CATEGORIES.OPTIONS,
            label: 'Display options',
            action: 'Opened display options',
        });
    };

    return (
        <div className='toolbar bg-white'>
            <div className={toolbarClassNames}>
                <ButtonDropdown
                    bsStyle='default'
                    variant='link'
                    onOpen={handleNewRule}
                    dropdownClassName='min-width-200'
                    title={
                        <div className='display-flex align-items-center gap-5'>
                            <span className='rioglyph rioglyph-plus-light text-size-16' />
                            <FormattedMessage id='intl-msg:notifications.newRule' defaultMessage='New rule' />
                        </div>
                    }
                    items={[
                        {
                            value: (
                                <div className='display-flex gap-15 align-items-center' style={{ minHeight: '24px' }}>
                                    <div className='margin-top--5'>
                                        <div className='rioglyph-icon-pair'>
                                            <span className='rioglyph rioglyph-notification text-size-18' />
                                            <span className='rioglyph rioglyph-plus bg-darkest' />
                                        </div>
                                    </div>
                                    <div className='line-height-18'>
                                        <FormattedMessage
                                            id='intl-msg:notifications.customRule'
                                            defaultMessage='Custom rule'
                                        />
                                    </div>
                                </div>
                            ),
                            onSelect: handleCustomRule,
                        },
                        {
                            value: (
                                <div className='display-flex gap-15 align-items-center' style={{ minHeight: '24px' }}>
                                    <div className='margin-top--4'>
                                        <div className='rioglyph-icon-pair'>
                                            <span className='rioglyph rioglyph-truck text-size-18' />
                                            <span className='rioglyph rioglyph-parking bg-darkest' />
                                        </div>
                                    </div>
                                    <div className='line-height-18'>
                                        <FormattedMessage
                                            id='intl-msg:notifications.vehicleDowntime'
                                            defaultMessage='Vehicle downtime'
                                        />
                                    </div>
                                </div>
                            ),
                            onSelect: handleVehicleDowntime,
                        },
                        {
                            value: (
                                <div className='display-flex gap-15 align-items-center' style={{ minHeight: '24px' }}>
                                    <div className='margin-top--4'>
                                        <div className='rioglyph-icon-pair'>
                                            <span className='rioglyph rioglyph-truck text-size-16' />
                                            <span className='rioglyph rioglyph-map-marker bg-darkest' />
                                        </div>
                                    </div>
                                    <div className='line-height-18'>
                                        <FormattedMessage
                                            id='intl-msg:notifications.noVehiclePosition'
                                            defaultMessage='No vehicle position'
                                        />
                                    </div>
                                </div>
                            ),
                            onSelect: handleVehiclePosition,
                        },
                    ]}
                />
                <ButtonDropdown
                    bsStyle='default'
                    variant='link'
                    onOpen={handleOpenOptions}
                    title={
                        <FormattedMessage id='intl-msg:notifications.displayOptions' defaultMessage='Display options' />
                    }
                    items={[
                        {
                            value: (
                                <div className='display-flex gap-10'>
                                    <span className='rioglyph rioglyph-envelope' />
                                    {showReadMessages ? (
                                        <FormattedMessage
                                            id='intl-msg:notifications.hideRead'
                                            defaultMessage='Hide read'
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id='intl-msg:notifications.showRead'
                                            defaultMessage='Show read'
                                        />
                                    )}
                                </div>
                            ),
                            onSelect: handleToggleReadMessages,
                        },
                        {
                            value: (
                                <div className='display-flex gap-10'>
                                    <div>
                                        <span className='rioglyph-icon-pair text-size-16'>
                                            <span className='rioglyph rioglyph-stack' />
                                            <span className='rioglyph rioglyph-ok text-size-10' />
                                        </span>
                                    </div>
                                    <span>
                                        <FormattedMessage
                                            id='intl-msg:notifications.markAllAsRead'
                                            defaultMessage='Mark all as read'
                                        />
                                    </span>
                                </div>
                            ),
                            onSelect: handleMarkAllAsRead,
                            disabled: !hasUnreadMessages,
                        },
                    ]}
                    pullRight
                />
            </div>
            <AccessInformationDialog
                show={isRuleInfoDialogShown}
                link={targetLink}
                onClose={handleCloseRuleDialog}
                onAccept={handleAcknowledgedRuleDialog}
            />
        </div>
    );
};
